import { type FC, type ReactNode, useLayoutEffect, useRef } from 'react';
import { useTrack } from 'src/contexts/AnalyticsContext';
import Modal from 'plaid-threads/deprecated/Modal'; // eslint-disable-line
import ModalBody from 'plaid-threads/deprecated/ModalBody'; // eslint-disable-line
import { PAGE } from 'src/lib/trackingEvents';

import styles from './ModalPane.module.scss'; // test

export type ModalPaneItemProps = {
  children: ReactNode;
  setIsModalOpen: (arg: boolean) => void;
  isModalOpen: boolean;
  trackingId?: string;
};

// Threads Modal sets overflow:hidden on <body>, which drops the classic
// scrollbar and the page jumps sideways. Reserving the gutter on <html>
// keeps the layout width stable. Padding <body> instead left an empty
// strip on the right, so don't do that. Overlay scrollbars take no space,
// so this only runs when a classic scrollbar has been measured.
const usePaywallScrollbarCompensation = (isModalOpen: boolean) => {
  const scrollbarWidthRef = useRef(0);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const liveWidth = window.innerWidth - root.clientWidth;

    if (!isModalOpen) {
      // Threads may still be removing overflow:hidden in this commit, which
      // makes the live width 0. Keep the last real measurement.
      if (liveWidth > 0) {
        scrollbarWidthRef.current = liveWidth;
      }
      return;
    }

    // The body class can land before this effect, so the scrollbar may
    // already be gone. Fall back to the width cached while it was visible.
    const width = liveWidth > 0 ? liveWidth : scrollbarWidthRef.current;
    if (width <= 0) {
      return;
    }

    const previousGutter = root.style.scrollbarGutter;
    root.style.scrollbarGutter = 'stable';

    return () => {
      root.style.scrollbarGutter = previousGutter;
    };
  }, [isModalOpen]);
};

const ModalPane: FC<ModalPaneItemProps> = ({
  children,
  setIsModalOpen,
  isModalOpen,
  trackingId = null,
}) => {
  const track = useTrack();
  const trackingIdPrefix = trackingId != null ? PAGE[trackingId] : null;

  usePaywallScrollbarCompensation(isModalOpen);

  if (!isModalOpen) {
    return null;
  }

  return (
    <Modal
      onRequestClose={() => {
        setIsModalOpen(false);
        if (trackingIdPrefix != null) {
          track({ type: `${trackingIdPrefix}_PAYWALL_MODAL_CLOSED` });
        }
      }}
      isOpen={isModalOpen}
      className={styles.paywallModal}
    >
      <ModalBody
        bodyClassName={styles.paywallModalBody}
        onClickCancel={() => {
          setIsModalOpen(false);
          if (trackingIdPrefix != null) {
            track({ type: `${trackingIdPrefix}_PAYWALL_MODAL_CLOSED` });
          }
        }}
      >
        {children}
      </ModalBody>
    </Modal>
  );
};

export default ModalPane;
